import { CSSProperties } from "react";
import { LangStore } from "../store/LangStore";
import { diaDeCalendario, formatearDia } from "./calendarDate";

export function convertTimeZone(date: Date | string, timeZone: string) {
  if (typeof date === "string") {
    return new Date(new Date(date).toLocaleString("en-US", { timeZone }));
  }
  return new Date(date.toLocaleString("en-US", { timeZone }));
}
export function downloadFromBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
}

export const disableOnTrue = (flag: boolean | undefined): CSSProperties => {
  return {
    pointerEvents: flag ? "none" : "initial",
    opacity: flag ? 0.15 : 1,
  };
};

export const removeFieldLang = (fieldName: string): string => {
  const postfixes = ["_en", "_es", "_pt"];
  for (const postfix of postfixes) {
    if (fieldName.endsWith(postfix)) {
      return fieldName.slice(0, -postfix.length);
    }
  }
  return fieldName;
};

export const resolveCurrentLang = (
  str: string | undefined,
  langStore: LangStore
): string => {
  if (!str) return "";
  const regex = /\{\{(?<lang>[^}]*)\}\}/g;
  const matches = [...str.matchAll(regex)];
  if (matches.length > 0) {
    const replacements: Record<string, string> = Object.fromEntries(
      [...str.matchAll(regex)].map((x: any) => {
        const val = Object.values(x.groups)[0];
        return [[`{{${val}}}`], JSON.parse(`{${val}}`)[langStore.language]];
      })
    );
    let replaced = str;
    for (const [key, value] of Object.entries(replacements)) {
      replaced = replaced.replace(key, value);
    }
    return replaced;
  }
  return str;
};

export const formatBytes = (bytes: number, decimals = 1) => {
  if (bytes === 0) return "0 Bytes";
  var k = 1024,
    dm = decimals || 2,
    sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const uploadFailureReason = (
  error: unknown,
  connectionLost: string
): string => {
  const response = (error as any)?.response;
  const serverMessage = response?.errors?.[0]?.message;
  if (serverMessage) {
    return response?.status && response.status !== 200
      ? `${serverMessage} (HTTP ${response.status})`
      : serverMessage;
  }
  if (error instanceof TypeError) return `${connectionLost}: ${error.message}`;
  return error instanceof Error ? error.message : String(error ?? "");
};

export const isNil = (x: any) => x === undefined || x === null;
export const nullToUndefined = <T>(x: T): Exclude<T, null> | undefined =>
  x === null ? undefined : (x as Exclude<T, null>);
export const nullToUndefinedRecursive = <T>(o: T): WithoutNull<T> => {
  if (Array.isArray(o)) {
    return o.map(nullToUndefinedRecursive) as WithoutNull<T>;
  }
  return o as WithoutNull<T>;
};

type WithoutNull<T> = T extends Array<infer I>
  ? Array<WithoutNull<I>>
  : T extends object
  ? { [K in keyof T]: WithoutNull<T[K]> }
  : Exclude<T, null>;

export const graphqlToDateTime = (dateTime: string | Date) => {
  return new Date(dateTime).toLocaleDateString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

export const graphqlToDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/**
 * Un día de calendario (un campo date, "AAAA-MM-DD") como DD/MM/AAAA, sin pasar por
 * la zona del navegador (common/calendarDate.ts). Un Date o un ISO con hora de un dato
 * viejo dan su día UTC. Sin valor, "..." (el nombre visible no la llama con un vacío:
 * pone el símbolo de vacío, como el server). Es el nombre visible de un date: el
 * server lo arma igual (graphqlToDate de su conversions.ts).
 */
export const graphqlToCalendarDate = (
  date: string | Date | null | undefined
) => {
  const day = diaDeCalendario(date);
  return day === null ? "..." : formatearDia(day, "es-AR");
};

/**
 * La hora con segundos, de 00 a 23: la única definición de graphqlToTime y
 * graphqlToDateTimeWithSeconds (es-AR, el día primero, como graphqlToDate).
 */
const TIME_WITH_SECONDS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
};

/** Solo la hora, con segundos: "14:35:12"; la medianoche, "00:05:07" (no "12:05:07 AM"). */
export const graphqlToTime = (value: string | Date) =>
  new Date(value).toLocaleTimeString("es-AR", TIME_WITH_SECONDS);

/** Fecha y hora con segundos: "01/10/2026, 14:35:12". */
export const graphqlToDateTimeWithSeconds = (value: string | Date) =>
  new Date(value).toLocaleString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...TIME_WITH_SECONDS,
  });

/**
 * Una clave de idempotencia para un guardado (el idempotencyKey_ de las
 * mutaciones de alta y de edición): un uuid v4 al azar. Sin crypto.randomUUID
 * (un navegador viejo, o fuera de https), uno armado con crypto.getRandomValues;
 * sin crypto (algunos entornos de test), con Math.random.
 */
export const newIdempotencyKey = (): string => {
  const webCrypto = typeof crypto !== "undefined" ? crypto : undefined;
  if (webCrypto && typeof webCrypto.randomUUID === "function")
    return webCrypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (webCrypto && typeof webCrypto.getRandomValues === "function")
    webCrypto.getRandomValues(bytes);
  else
    for (let i = 0; i < bytes.length; i++)
      bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    ""
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Lo que manda un pedido, comparable: dos intentos de guardar con la misma huella
 * mandan lo mismo (un archivo se compara por nombre, tamaño, tipo y fecha).
 */
export const requestFingerprint = (value: unknown): string =>
  JSON.stringify(value, (_key, v) =>
    typeof Blob !== "undefined" && v instanceof Blob
      ? { blob: [(v as File).name, v.size, v.type, (v as File).lastModified] }
      : v
  );

/**
 * El locale para mostrar nombres de mes o de día (los gráficos): el idioma de la
 * app con el día antes que el mes ("1 Oct", no "Oct 1"). Las fechas numéricas van
 * con graphqlToDate y graphqlToDateTime.
 */
export const dayFirstLocale = (language: string): string =>
  (({ es: "es-AR", en: "en-GB", pt: "pt-BR" } as Record<string, string>)[
    language
  ] ?? language);

export const normalizeAndValidateEmail = (email: string): string | null => {
  const normalizedEmail = email.normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const tested =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
      normalizedEmail
    );
  return tested ? normalizedEmail : null;
};

export function getUrlFromBlob(blob: Blob) {
  return URL.createObjectURL(blob);
}

export function isGenerator(value: any): value is Generator {
  return Boolean(
    value &&
      typeof value === "object" &&
      typeof value.next === "function" &&
      typeof value.throw === "function" &&
      typeof value.return === "function"
  );
}

export class JsonParseError extends Error {
  constructor(public fieldName: string, public originalError: Error) {
    super(`Invalid JSON in field "${fieldName}": ${originalError.message}`);
    this.name = "JsonParseError";
  }
}

export function safeJsonParse(value: string, fieldName: string): any {
  // Un campo vacío no es JSON mal formado: es un campo sin valor. Los campos json
  // opcionales llegan del formulario como cadena vacía —así los representa el propio
  // estado del form—, y `JSON.parse("")` tira "Unexpected end of JSON input": el
  // usuario recibía "JSON inválido" al grabar sobre un campo que nunca tocó, sin
  // manera de arreglarlo desde la pantalla. Vacío va como null, y el mensaje de JSON
  // inválido queda para lo que de verdad lo es: texto mal formado.
  if (value === null || value === undefined) return null;
  // Ya parseado (algún caller lo pasa como objeto): devolverlo tal cual. `JSON.parse`
  // de un objeto lo convierte a "[object Object]" y falla.
  if (typeof value !== "string") return value;
  if (value.trim() === "") return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    throw new JsonParseError(fieldName, e as Error);
  }
}
